import { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "./anim";
import { PORTFOLIO, WORK_CATS } from "./data";

const spanFor = (i, len) =>
  i === len - 1 && len % 2 === 1 ? "md:col-span-12" : ["md:col-span-7", "md:col-span-5", "md:col-span-5", "md:col-span-7"][i % 4];

const TiltCard = ({ item, index, span }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 200, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 200, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <Reveal className={span} delay={(index % 2) * 0.1}>
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        data-testid={`portfolio-item-${index}`}
        className="group relative h-[300px] overflow-hidden rounded-2xl border border-[hsl(var(--border))] md:h-[420px]"
      >
        <motion.img
          src={item.img}
          alt={item.title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-7" style={{ transform: "translateZ(40px)" }}>
          <span className="mb-2 w-fit rounded-full border border-white/25 bg-black/30 px-3 py-1 text-[11px] uppercase tracking-widest text-white backdrop-blur">
            {item.tag}
          </span>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-head text-2xl font-semibold text-white md:text-3xl">
              {item.title}
            </h3>
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))] opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <ArrowUpRight size={18} />
            </span>
          </div>
        </div>
      </motion.div>
    </Reveal>
  );
};

const FilterBar = ({ cat, pickCat }) => (
  <Reveal>
    <div className="mb-10 flex flex-wrap gap-2">
      {WORK_CATS.map((c) => (
        <button
          key={c}
          onClick={() => pickCat(c)}
          data-testid={`work-filter-${c.toLowerCase()}`}
          className={`rounded-full border px-6 py-2.5 text-sm font-medium transition-colors duration-300 ${
            cat === c
              ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-[hsl(var(--primary-fg))]"
              : "border-[hsl(var(--border))] text-[hsl(var(--muted))] hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--fg))]"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  </Reveal>
);

export const Portfolio = () => {
  const [cat, setCat] = useState("All");
  const [visible, setVisible] = useState(9);
  const filtered = cat === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.cat === cat);
  const items = filtered.slice(0, visible);

  const pickCat = (c) => {
    setCat(c);
    setVisible(9);
  };

  return (
  <section
    id="work"
    data-testid="portfolio-section"
    className="relative mx-auto max-w-[1400px] px-6 py-28 md:px-10 md:py-36"
  >
    <div className="mb-14 grid gap-6 md:grid-cols-2 md:items-end">
      <Reveal>
        <p className="mb-4 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-[hsl(var(--muted))]">
          <span className="h-px w-8 bg-[hsl(var(--primary))]" /> Selected work
        </p>
        <h2 className="font-head text-4xl font-semibold leading-[1.02] sm:text-5xl lg:text-6xl">
          Work that makes<br /> brands <span className="italic text-[hsl(var(--primary))]">unforgettable.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="max-w-md text-base text-[hsl(var(--muted))] md:ml-auto md:text-right">
          A glimpse of the worlds we build — branding, film, 3D and campaigns delivered for founders and teams around the globe.
        </p>
      </Reveal>
    </div>

    <FilterBar cat={cat} pickCat={pickCat} />

    <div key={cat} className="grid gap-5 md:grid-cols-12" style={{ perspective: 1200 }}>
      {items.map((item, i) => (
        <TiltCard key={item.title} item={item} index={i} span={spanFor(i, items.length)} />
      ))}
    </div>

    {filtered.length > visible && (
      <div className="mt-12 flex justify-center">
        <button
          onClick={() => setVisible((v) => v + 9)}
          data-testid="portfolio-load-more-btn"
          className="group flex items-center gap-2 rounded-full border border-[hsl(var(--primary))] px-9 py-4 text-base font-semibold text-[hsl(var(--primary))] transition-colors duration-300 hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-fg))]"
        >
          View More Work
          <span className="text-sm opacity-70">({filtered.length - visible} left)</span>
        </button>
      </div>
    )}
  </section>
  );
};
